import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { applyThemeColors } from '../config/theme';
import { monthlyPriceFor, formatPrice } from '../config/pricing';
import { useConfig } from '../context/ConfigContext';
import Breadcrumbs, { buildSettingsBreadcrumbs } from '../components/Breadcrumbs';
import Alert from '../components/Alert';
import { SkeletonForm } from '../components/SkeletonLoader';

// Pre-built color themes
const COLOR_THEMES = [
  {
    id: 'custom',
    name: 'Custom',
    colors: null // No preset - manual selection
  },
  {
    id: 'forest-green',
    name: 'Forest Green',
    colors: {
      primaryColor: '#2e7d32',
      primaryDark: '#1e4620',
      secondaryColor: '#f4a460',
      secondaryMuted: '#d4873e',
      accentColor: '#1565c0',
      lightBackground: '#f0f8f0'
    }
  },
  {
    id: 'ocean-blue',
    name: 'Ocean Blue',
    colors: {
      primaryColor: '#1e3a5f',
      primaryDark: '#0c2340',
      secondaryColor: '#f4a460',
      secondaryMuted: '#d4873e',
      accentColor: '#3b82f6',
      lightBackground: '#f0f5fa'
    }
  },
  {
    id: 'modern-teal',
    name: 'Modern Teal',
    colors: {
      primaryColor: '#0d9488',
      primaryDark: '#065f53',
      secondaryColor: '#f4a460',
      secondaryMuted: '#d4873e',
      accentColor: '#14b8a6',
      lightBackground: '#f0fdf9'
    }
  },
  {
    id: 'royal-purple',
    name: 'Royal Purple',
    colors: {
      primaryColor: '#6b21a8',
      primaryDark: '#4c1d95',
      secondaryColor: '#f4a460',
      secondaryMuted: '#d4873e',
      accentColor: '#a855f7',
      lightBackground: '#faf5ff'
    }
  },
  {
    id: 'sunset-orange',
    name: 'Sunset Orange',
    colors: {
      primaryColor: '#ea580c',
      primaryDark: '#c2410c',
      secondaryColor: '#fbbf24',
      secondaryMuted: '#d97706',
      accentColor: '#f97316',
      lightBackground: '#fff7ed'
    }
  },
  {
    id: 'slate-gray',
    name: 'Slate Gray',
    colors: {
      primaryColor: '#475569',
      primaryDark: '#334155',
      secondaryColor: '#f4a460',
      secondaryMuted: '#d4873e',
      accentColor: '#64748b',
      lightBackground: '#f8fafc'
    }
  },
  {
    id: 'earth-brown',
    name: 'Earth Brown',
    colors: {
      primaryColor: '#78592a',
      primaryDark: '#5c4420',
      secondaryColor: '#d4a574',
      secondaryMuted: '#b8956a',
      accentColor: '#a67c52',
      lightBackground: '#faf6f1'
    }
  },
  {
    id: 'crimson-red',
    name: 'Crimson Red',
    colors: {
      primaryColor: '#dc2626',
      primaryDark: '#b91c1c',
      secondaryColor: '#f4a460',
      secondaryMuted: '#d4873e',
      accentColor: '#ef4444',
      lightBackground: '#fef2f2'
    }
  }
];

function BrandingSettings() {
  const navigate = useNavigate();
  const { isDemoMode, TIERS, currentTenant } = useConfig();
  const demoDisabled = isDemoMode();
  const [loading, setLoading] = useState(true);
  // Fetch tier directly from billing API for accuracy
  const [currentTier, setCurrentTier] = useState(TIERS.FREE_TRIAL);

  // Compute tier-based properties
  const logoUploadAllowed = [TIERS.PRO, TIERS.ENTERPRISE].includes(currentTier);
  const isFreeTrial = () => currentTier === TIERS.FREE_TRIAL;
  const isStarter = () => currentTier === TIERS.STARTER;
  const logoUpgradeMessage = currentTier === TIERS.FREE_TRIAL
    ? `Upgrade to Starter (${formatPrice(monthlyPriceFor('starter'))}/mo) to customize your company name, or PRO (${formatPrice(monthlyPriceFor('pro'))}/mo) to upload a custom logo.`
    : currentTier === TIERS.STARTER
      ? `Upgrade to PRO (${formatPrice(monthlyPriceFor('pro'))}/mo) to upload a custom logo.`
      : null;

  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [hasCustomColors, setHasCustomColors] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tenantSubdomain, setTenantSubdomain] = useState(null);

  // Default colors from server config
  const [defaults, setDefaults] = useState({});

  // Contact info state
  const [contactInfo, setContactInfo] = useState({
    companyName: '',
    companyLegalName: '',
    companyAddress: '',
    companyPhone: '',
    companyEmail: '',
    websiteUrl: '',
    tagline: '',
    // What search results and link previews print under the title. Empty means
    // the platform composes one from the company name; see the field's help.
    metaDescription: '',
    // Empty, NOT the platform default. Every key in this object is the
    // tenant's own stored value and nothing else; see resolvedContactInfo.
    businessHours: '',
    // Blank is a valid saved state, not "unset" -- see the field's help text.
    signerName: ''
  });
  const [originalContactInfo, setOriginalContactInfo] = useState({});

  // WHAT THE PLATFORM WOULD PRINT IF THIS TENANT SET NOTHING.
  //
  // Display only. It is shown as placeholder text and is never edited, never
  // included in hasChanges, and never sent back on Save. That separation is
  // the point: the GET used to return these values IN contactInfo, so opening
  // this page and pressing Save wrote a computed value the admin never chose
  // into the tenant's own rows, permanently detaching them from the default
  // they had been inheriting.
  const [resolvedContactInfo, setResolvedContactInfo] = useState({});

  // Logo state
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoLightUrl, setLogoLightUrl] = useState(null);
  const [defaultLogoUrl, setDefaultLogoUrl] = useState('/images/deedstack-logos/logo-horizontal-final.svg');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingLogoLight, setUploadingLogoLight] = useState(false);
  const [logoError, setLogoError] = useState('');
  const [logoLightError, setLogoLightError] = useState('');

  // The browser tab icon, and the image social platforms show when a link to
  // this storefront is shared. Both are separate uploads rather than a crop of
  // the logo: a horizontal logo is the wrong shape for a 16px square and the
  // wrong shape again for a 1200x630 card.
  const [faviconUrl, setFaviconUrl] = useState(null);
  const [ogImageUrl, setOgImageUrl] = useState(null);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const [uploadingOgImage, setUploadingOgImage] = useState(false);
  const [faviconError, setFaviconError] = useState('');
  const [ogImageError, setOgImageError] = useState('');
  const [useTextLogo, setUseTextLogo] = useState(false); // PRO+ toggle: display text instead of logo
  const [originalUseTextLogo, setOriginalUseTextLogo] = useState(false);

  // The storefront's starting theme for a visitor who has never chosen one.
  // Stored per tenant in site_settings under the 'branding' category. It is a
  // starting point and not a lock: every visitor can still switch themes, and
  // their own choice is remembered from then on.
  const [defaultTheme, setDefaultTheme] = useState('system');
  const [originalDefaultTheme, setOriginalDefaultTheme] = useState('system');

  // Current editing colors
  const [colors, setColors] = useState({
    primaryColor: '#2e7d32',
    primaryDark: '#1e4620',
    secondaryColor: '#f4a460',
    secondaryMuted: '#d4873e',
    accentColor: '#1565c0',
    lightBackground: '#f0f8f0'
  });

  // Original colors to track changes
  const [originalColors, setOriginalColors] = useState({});

  // Detect which theme matches current colors
  const getMatchingTheme = (currentColors) => {
    for (const theme of COLOR_THEMES) {
      if (!theme.colors) continue; // Skip 'Custom'
      const matches = Object.keys(theme.colors).every(
        key => theme.colors[key]?.toLowerCase() === currentColors[key]?.toLowerCase()
      );
      if (matches) return theme.id;
    }
    return 'custom';
  };

  const handleThemeChange = (themeId) => {
    if (themeId === 'custom') return; // Don't change anything for custom

    const theme = COLOR_THEMES.find(t => t.id === themeId);
    if (theme && theme.colors) {
      const newColors = { ...colors, ...theme.colors };
      setColors(newColors);
      setHasChanges(
        JSON.stringify(newColors) !== JSON.stringify(originalColors) ||
        JSON.stringify(contactInfo) !== JSON.stringify(originalContactInfo) ||
        useTextLogo !== originalUseTextLogo ||
        defaultTheme !== originalDefaultTheme
      );
      applyThemeColors(newColors);
    }
  };

  useEffect(() => {
    fetchBrandingSettings();
    // Load tenant subdomain from localStorage
    const adminUser = localStorage.getItem('adminUser');
    if (adminUser) {
      try {
        const parsed = JSON.parse(adminUser);
        if (parsed.tenantSubdomain) {
          setTenantSubdomain(parsed.tenantSubdomain);
        }
      } catch (e) {
        console.error('Error parsing adminUser:', e);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBrandingSettings = async () => {
    try {
      const token = localStorage.getItem('adminToken');

      // Fetch branding settings and billing status in parallel
      const [brandingResponse, billingResponse] = await Promise.all([
        api.get('/admin/settings/branding', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_API_URL}/billing/status`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(r => r.ok ? r.json() : null).catch(() => null)
      ]);

      setColors(brandingResponse.data.colors);
      setOriginalColors(brandingResponse.data.colors);
      setDefaults(brandingResponse.data.defaults);
      setHasCustomColors(brandingResponse.data.hasCustomColors);

      // Load contact info. contactInfo is the tenant's RAW stored values, so
      // what goes into state is what a Save will write back.
      if (brandingResponse.data.contactInfo) {
        setContactInfo(brandingResponse.data.contactInfo);
        setOriginalContactInfo(brandingResponse.data.contactInfo);
      }

      // The resolved chain, for placeholders. Deliberately not fed into
      // originalContactInfo, so it cannot register as a pending change.
      if (brandingResponse.data.resolvedContactInfo) {
        setResolvedContactInfo(brandingResponse.data.resolvedContactInfo);
      }

      // Load logo info
      if (brandingResponse.data.logoUrl) {
        setLogoUrl(brandingResponse.data.logoUrl);
      }
      if (brandingResponse.data.logoLightUrl) {
        setLogoLightUrl(brandingResponse.data.logoLightUrl);
      }
      if (brandingResponse.data.faviconUrl) {
        setFaviconUrl(brandingResponse.data.faviconUrl);
      }
      if (brandingResponse.data.ogImageUrl) {
        setOgImageUrl(brandingResponse.data.ogImageUrl);
      }
      if (brandingResponse.data.defaultLogoUrl) {
        setDefaultLogoUrl(brandingResponse.data.defaultLogoUrl);
      }
      // Load header display preference
      if (brandingResponse.data.useTextLogo !== undefined) {
        setUseTextLogo(brandingResponse.data.useTextLogo);
        setOriginalUseTextLogo(brandingResponse.data.useTextLogo);
      }

      // Load the storefront's default theme. The server always resolves this to
      // a valid value, so there is no branch for a missing one.
      if (brandingResponse.data.defaultTheme) {
        setDefaultTheme(brandingResponse.data.defaultTheme);
        setOriginalDefaultTheme(brandingResponse.data.defaultTheme);
      }

      // Set tier from billing status
      if (billingResponse?.tier) {
        const tierMap = {
          'free_trial': TIERS.FREE_TRIAL,
          'starter': TIERS.STARTER,
          'pro': TIERS.PRO,
          'enterprise': TIERS.ENTERPRISE
        };
        setCurrentTier(tierMap[billingResponse.tier] || TIERS.FREE_TRIAL);
      }

      setError('');
    } catch (err) {
      console.error('Failed to fetch branding settings:', err);
      setError('Failed to load branding settings');
    } finally {
      setLoading(false);
    }
  };

  const handleColorChange = (key, value) => {
    const newColors = { ...colors, [key]: value };
    setColors(newColors);
    setHasChanges(
      JSON.stringify(newColors) !== JSON.stringify(originalColors) ||
      JSON.stringify(contactInfo) !== JSON.stringify(originalContactInfo) ||
      useTextLogo !== originalUseTextLogo ||
      defaultTheme !== originalDefaultTheme
    );

    // Apply live preview
    applyThemeColors(newColors);
  };

  const handleContactInfoChange = (key, value) => {
    const newContactInfo = { ...contactInfo, [key]: value };
    setContactInfo(newContactInfo);
    setHasChanges(
      JSON.stringify(newContactInfo) !== JSON.stringify(originalContactInfo) ||
      JSON.stringify(colors) !== JSON.stringify(originalColors) ||
      useTextLogo !== originalUseTextLogo ||
      defaultTheme !== originalDefaultTheme
    );
  };

  /**
   * The storefront's default theme. Unlike the colour pickers this has no live
   * preview: it changes what a NEW visitor starts in, not what this admin is
   * currently looking at, and repainting the settings page on a click would
   * suggest otherwise. The admin's own theme is the control in the corner.
   */
  const handleDefaultThemeChange = (value) => {
    setDefaultTheme(value);
    setHasChanges(
      value !== originalDefaultTheme ||
      JSON.stringify(colors) !== JSON.stringify(originalColors) ||
      JSON.stringify(contactInfo) !== JSON.stringify(originalContactInfo) ||
      useTextLogo !== originalUseTextLogo
    );
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('adminToken');
      await api.post('/admin/settings/branding',
        { colors, contactInfo, useTextLogo, defaultTheme },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setOriginalColors(colors);
      setOriginalContactInfo(contactInfo);
      setOriginalUseTextLogo(useTextLogo);
      setOriginalDefaultTheme(defaultTheme);
      setHasChanges(false);
      setHasCustomColors(true);
      setSuccess('Branding settings saved successfully! Changes will be visible to all users.');
      window.scrollTo(0, 0);
    } catch (err) {
      console.error('Failed to save branding settings:', err);
      setError(err.response?.data?.error || 'Failed to save branding settings');
      // Revert preview on error
      applyThemeColors(originalColors);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset all colors to defaults? This will affect all users.')) {
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('adminToken');
      const response = await api.delete('/admin/settings/branding', {
        headers: { Authorization: `Bearer ${token}` }
      });

      setColors(response.data.colors);
      setOriginalColors(response.data.colors);
      setHasChanges(false);
      setHasCustomColors(false);
      setSuccess('Colors reset to defaults successfully!');

      // Apply default colors
      applyThemeColors(response.data.colors);
    } catch (err) {
      console.error('Failed to reset branding settings:', err);
      setError('Failed to reset branding settings');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      // Revert preview
      applyThemeColors(originalColors);
    }
    navigate('/admin/settings-hub');
  };

  // Logo upload handler
  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      setLogoError('Invalid file type. Please upload PNG, JPG, or SVG.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setLogoError('File too large. Maximum size is 5MB.');
      return;
    }

    setUploadingLogo(true);
    setLogoError('');

    try {
      const token = localStorage.getItem('adminToken');
      const formData = new FormData();
      formData.append('logo', file);

      const response = await api.post('/admin/upload-logo', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setLogoUrl(response.data.logoUrl);
      setSuccess('Logo uploaded successfully!');
      window.scrollTo(0, 0);
    } catch (err) {
      console.error('Failed to upload logo:', err);
      setLogoError(err.response?.data?.error || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  // Delete custom logo
  const handleDeleteLogo = async () => {
    if (!window.confirm('Remove custom logo? The default logo will be used instead.')) {
      return;
    }

    setUploadingLogo(true);
    setLogoError('');

    try {
      const token = localStorage.getItem('adminToken');
      await api.delete('/admin/logo', {
        headers: { Authorization: `Bearer ${token}` }
      });

      setLogoUrl(null);
      setSuccess('Custom logo removed. Default logo will be used.');
      window.scrollTo(0, 0);
    } catch (err) {
      console.error('Failed to delete logo:', err);
      setLogoError(err.response?.data?.error || 'Failed to delete logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  // Light logo upload handler
  const handleLogoLightUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      setLogoLightError('Invalid file type. Please upload PNG, JPG, or SVG.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setLogoLightError('File too large. Maximum size is 5MB.');
      return;
    }

    setUploadingLogoLight(true);
    setLogoLightError('');

    try {
      const token = localStorage.getItem('adminToken');
      const formData = new FormData();
      formData.append('logo', file);

      const response = await api.post('/admin/upload-logo-light', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setLogoLightUrl(response.data.logoLightUrl);
      setSuccess('Light logo uploaded successfully!');
      window.scrollTo(0, 0);
    } catch (err) {
      console.error('Failed to upload light logo:', err);
      setLogoLightError(err.response?.data?.error || 'Failed to upload light logo');
    } finally {
      setUploadingLogoLight(false);
    }
  };

  // Delete custom light logo
  const handleDeleteLogoLight = async () => {
    if (!window.confirm('Remove light logo? The regular logo will be used on dark backgrounds.')) {
      return;
    }

    setUploadingLogoLight(true);
    setLogoLightError('');

    try {
      const token = localStorage.getItem('adminToken');
      await api.delete('/admin/logo-light', {
        headers: { Authorization: `Bearer ${token}` }
      });

      setLogoLightUrl(null);
      setSuccess('Light logo removed.');
      window.scrollTo(0, 0);
    } catch (err) {
      console.error('Failed to delete light logo:', err);
      setLogoLightError(err.response?.data?.error || 'Failed to delete light logo');
    } finally {
      setUploadingLogoLight(false);
    }
  };

  // Favicon upload handler
  const handleFaviconUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Some browsers report an .ico as application/octet-stream, so the
    // extension is accepted alongside the media types. The server applies the
    // same rule; this one only saves a round trip.
    const allowedTypes = ['image/png', 'image/svg+xml', 'image/x-icon', 'image/vnd.microsoft.icon'];
    const looksLikeIco = /\.ico$/i.test(file.name || '');
    if (!allowedTypes.includes(file.type) && !looksLikeIco) {
      setFaviconError('Invalid file type. Please upload an ICO, PNG, or SVG.');
      return;
    }

    if (file.size > 1 * 1024 * 1024) {
      setFaviconError('File too large. Maximum size is 1MB.');
      return;
    }

    setUploadingFavicon(true);
    setFaviconError('');

    try {
      const token = localStorage.getItem('adminToken');
      const formData = new FormData();
      formData.append('favicon', file);

      const response = await api.post('/admin/upload-favicon', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setFaviconUrl(response.data.faviconUrl);
      setSuccess('Favicon uploaded successfully!');
      window.scrollTo(0, 0);
    } catch (err) {
      console.error('Failed to upload favicon:', err);
      setFaviconError(err.response?.data?.error || 'Failed to upload favicon');
    } finally {
      setUploadingFavicon(false);
    }
  };

  const handleDeleteFavicon = async () => {
    if (!window.confirm('Remove custom favicon? Your logo will be used instead.')) {
      return;
    }

    setUploadingFavicon(true);
    setFaviconError('');

    try {
      const token = localStorage.getItem('adminToken');
      await api.delete('/admin/favicon', {
        headers: { Authorization: `Bearer ${token}` }
      });

      setFaviconUrl(null);
      setSuccess('Custom favicon removed.');
      window.scrollTo(0, 0);
    } catch (err) {
      console.error('Failed to delete favicon:', err);
      setFaviconError(err.response?.data?.error || 'Failed to delete favicon');
    } finally {
      setUploadingFavicon(false);
    }
  };

  // Social share image upload handler
  const handleOgImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // No SVG. The platforms that read og:image do not render it and show a
    // blank card, which is worse than falling back to the logo.
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setOgImageError('Invalid file type. Please upload a PNG, JPG, or WebP.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setOgImageError('File too large. Maximum size is 5MB.');
      return;
    }

    setUploadingOgImage(true);
    setOgImageError('');

    try {
      const token = localStorage.getItem('adminToken');
      const formData = new FormData();
      formData.append('image', file);

      const response = await api.post('/admin/upload-og-image', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setOgImageUrl(response.data.ogImageUrl);
      setSuccess('Social share image uploaded successfully!');
      window.scrollTo(0, 0);
    } catch (err) {
      console.error('Failed to upload social share image:', err);
      setOgImageError(err.response?.data?.error || 'Failed to upload social share image');
    } finally {
      setUploadingOgImage(false);
    }
  };

  const handleDeleteOgImage = async () => {
    if (!window.confirm('Remove social share image? Your logo will be used instead.')) {
      return;
    }

    setUploadingOgImage(true);
    setOgImageError('');

    try {
      const token = localStorage.getItem('adminToken');
      await api.delete('/admin/og-image', {
        headers: { Authorization: `Bearer ${token}` }
      });

      setOgImageUrl(null);
      setSuccess('Social share image removed.');
      window.scrollTo(0, 0);
    } catch (err) {
      console.error('Failed to delete social share image:', err);
      setOgImageError(err.response?.data?.error || 'Failed to delete social share image');
    } finally {
      setUploadingOgImage(false);
    }
  };

  // Color picker configuration
  const colorFields = [
    {
      key: 'primaryColor',
      label: 'Primary Color',
      description: 'Main brand color used for headers, navigation, and primary buttons'
    },
    {
      key: 'primaryDark',
      label: 'Primary Dark',
      description: 'Darker shade of primary color for hover states and accents'
    },
    {
      key: 'secondaryColor',
      label: 'Secondary Color',
      description: 'Accent color for call-to-action buttons and highlights'
    },
    {
      key: 'secondaryMuted',
      label: 'Secondary Muted',
      description: 'Muted version of secondary color for hover states'
    },
    {
      key: 'accentColor',
      label: 'Accent Color',
      description: 'Used for links and interactive elements'
    },
    {
      key: 'lightBackground',
      label: 'Light Background',
      description: 'Background color for alternating sections and highlights'
    }
  ];

  if (loading) {
    return (
      <div className="admin-dashboard-container">
        <div className="admin-hub-header">
          <Breadcrumbs items={buildSettingsBreadcrumbs('Branding')} />
          <button onClick={handleCancel} className="admin-hub-back-btn">
            <span className="admin-hub-back-arrow">←</span>
            Back to Settings
          </button>
          <h1 className="admin-page-title">Branding Settings</h1>
        </div>
        <div className="card">
          <SkeletonForm fields={4} />
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-container">
      {/* Header with Breadcrumbs */}
      <div className="admin-hub-header">
        <Breadcrumbs items={buildSettingsBreadcrumbs('Branding')} />
        <button onClick={handleCancel} className="admin-hub-back-btn">
          <span className="admin-hub-back-arrow">←</span>
          Back to Settings
        </button>
        <h1 className="admin-page-title">Branding Settings</h1>
        <p className="admin-hub-subtitle">Customize your platform's color theme</p>
      </div>

      {/* Alert Messages */}
      {error && (
        <Alert type="error" message={error} onDismiss={() => setError('')} />
      )}
      {success && (
        <Alert type="success" message={success} onDismiss={() => setSuccess('')} />
      )}

      {/* Site URL Info Section */}
      {(currentTenant?.customDomain || tenantSubdomain) && (
        <div className="card" style={{ padding: '20px', marginBottom: '30px', backgroundColor: 'var(--color-info-bg)', border: '1px solid var(--color-info-border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <span style={{ fontSize: '24px' }}>🌐</span>
            <div>
              <h3 style={{ margin: '0 0 5px 0', color: 'var(--color-info-text)', fontSize: '16px' }}>Your Site URL</h3>
              <p style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: 'var(--color-info-text)' }}>
                <a
                  href={`https://${currentTenant?.customDomain || `${tenantSubdomain}.deedstack.com`}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--color-info-text)', textDecoration: 'none' }}
                >
                  {currentTenant?.customDomain || `${tenantSubdomain}.deedstack.com`}
                </a>
              </p>
              <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: 'var(--color-text-light)' }}>
                This is your unique site URL. Share it with your customers!
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Logo Section */}
      <div className="card" style={{ padding: '25px', marginBottom: '30px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
          <h2 style={{ marginTop: 0, marginBottom: '0', color: 'var(--color-brand-text)' }}>
            Logo & Header Display
          </h2>
          {/* Tier Badge */}
          <span style={{
            padding: '4px 12px',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: '600',
            backgroundColor: currentTier === TIERS.ENTERPRISE ? '#7c3aed' :
                            currentTier === TIERS.PRO ? '#2563eb' :
                            currentTier === TIERS.STARTER ? '#059669' : '#6b7280',
            color: 'white'
          }}>
            {currentTier === TIERS.ENTERPRISE ? 'ENTERPRISE' :
             currentTier === TIERS.PRO ? 'PRO' :
             currentTier === TIERS.STARTER ? 'STARTER' : 'FREE TRIAL'}
          </span>
        </div>

        {/* Upgrade Message for lower tiers */}
        {logoUpgradeMessage && (
          <div style={{
            backgroundColor: 'var(--color-warning-bg)',
            border: '1px solid var(--color-warning-border)',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <span style={{ fontSize: '18px' }}>⬆️</span>
            <div>
              <p style={{ margin: 0, fontSize: '14px', color: 'var(--color-warning-text)' }}>
                {logoUpgradeMessage}
              </p>
            </div>
          </div>
        )}

        {/* FREE_TRIAL: Show DeedStack branding message */}
        {isFreeTrial() && (
          <div style={{
            backgroundColor: 'var(--color-info-bg)',
            border: '1px solid var(--color-info-border)',
            borderRadius: '8px',
            padding: '20px',
            textAlign: 'center'
          }}>
            <img
              src="/images/deedstack-logos/logo-horizontal-final.svg"
              alt="DeedStack"
              style={{ maxWidth: '200px', marginBottom: '15px' }}
            />
            <p style={{ margin: 0, fontSize: '14px', color: 'var(--color-info-text)' }}>
              During your free trial, your site displays the DeedStack logo.
              Upgrade to customize your branding!
            </p>
          </div>
        )}

        {/* STARTER: Show company name display */}
        {isStarter() && (
          <div style={{
            backgroundColor: 'var(--color-success-bg)',
            border: '1px solid var(--color-success-border)',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '20px'
          }}>
            <p style={{ fontSize: '14px', color: 'var(--color-success-text)', margin: '0 0 10px 0' }}>
              Your header displays your company name as styled text:
            </p>
            <div style={{
              backgroundColor: 'var(--color-primary)',
              padding: '15px 25px',
              borderRadius: '6px',
              display: 'inline-block'
            }}>
              <span style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: 'white',
                letterSpacing: '-0.5px'
              }}>
                {contactInfo.companyName || 'Your Company Name'}
              </span>
            </div>
            <p style={{ margin: '15px 0 0 0', fontSize: '13px', color: 'var(--color-success-text)' }}>
              Upgrade to PRO to upload a custom logo!
            </p>
          </div>
        )}

        {/* PRO/ENTERPRISE: Full logo controls */}
        {logoUploadAllowed && (
          <>
            {/* Display Preference Toggle */}
            <div style={{
              backgroundColor: 'var(--color-surface-sunken)',
              borderRadius: '8px',
              padding: '15px 20px',
              marginBottom: '20px',
              border: '1px solid var(--color-border)'
            }}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: '600', color: 'var(--color-text)' }}>
                Header Display Preference
              </label>
              <div style={{ display: 'flex', gap: '15px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  padding: '10px 15px',
                  borderRadius: '6px',
                  border: !useTextLogo ? '2px solid var(--color-primary)' : '2px solid #ddd',
                  backgroundColor: !useTextLogo ? 'rgba(var(--color-primary-rgb), 0.05)' : 'white'
                }}>
                  <input
                    type="radio"
                    name="displayPreference"
                    checked={!useTextLogo}
                    onChange={() => { setUseTextLogo(false); setHasChanges(true); }}
                  />
                  <span>Display Logo Image</span>
                </label>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  padding: '10px 15px',
                  borderRadius: '6px',
                  border: useTextLogo ? '2px solid var(--color-primary)' : '2px solid #ddd',
                  backgroundColor: useTextLogo ? 'rgba(var(--color-primary-rgb), 0.05)' : 'white'
                }}>
                  <input
                    type="radio"
                    name="displayPreference"
                    checked={useTextLogo}
                    onChange={() => { setUseTextLogo(true); setHasChanges(true); }}
                  />
                  <span>Display Company Name as Text</span>
                </label>
              </div>
              {hasChanges && (
                <p style={{
                  margin: '10px 0 0 0',
                  fontSize: '13px',
                  color: 'var(--color-info-text)',
                  fontStyle: 'italic'
                }}>
                  Scroll down and click "Save Changes" to apply
                </p>
              )}
            </div>

            {/* Text Preview (when text is selected) */}
            {useTextLogo && (
              <div style={{
                backgroundColor: 'var(--color-success-bg)',
                border: '1px solid var(--color-success-border)',
                borderRadius: '8px',
                padding: '20px',
                marginBottom: '20px',
                textAlign: 'center'
              }}>
                <p style={{ fontSize: '13px', color: 'var(--color-success-text)', margin: '0 0 10px 0' }}>
                  Your header will display:
                </p>
                <div style={{
                  backgroundColor: 'var(--color-primary)',
                  padding: '15px 25px',
                  borderRadius: '6px',
                  display: 'inline-block'
                }}>
                  <span style={{
                    fontSize: '1.5rem',
                    fontWeight: '700',
                    color: 'white',
                    letterSpacing: '-0.5px'
                  }}>
                    {contactInfo.companyName || 'Your Company Name'}
                  </span>
                </div>
              </div>
            )}

            {/* Logo Upload (when logo is selected) */}
            {!useTextLogo && (
              <>
                <p style={{ fontSize: '14px', color: 'var(--color-text-light)', marginBottom: '20px' }}>
                  Upload your company logo (PNG, JPG, or SVG, max 5MB)
                </p>

                {logoError && (
                  <div className="alert alert-danger" style={{ marginBottom: '15px' }}>
                    {logoError}
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: '30px' }}>
                  {/* Logo Preview */}
                  <div style={{
                    border: '2px dashed var(--color-border)',
                    borderRadius: '8px',
                    padding: '20px',
                    backgroundColor: 'var(--color-surface-sunken)',
                    minWidth: '200px',
                    textAlign: 'center'
                  }}>
                    <p style={{ fontSize: '12px', color: 'var(--color-text-light)', margin: '0 0 10px 0' }}>
                      {logoUrl ? 'Current Logo' : 'Default Logo'}
                    </p>
                    <img
                      src={logoUrl || defaultLogoUrl}
                      alt="Company Logo"
                      style={{
                        maxWidth: '180px',
                        maxHeight: '80px',
                        objectFit: 'contain'
                      }}
                    />
                  </div>

                  {/* Upload Controls */}
                  <div style={{ flex: 1 }}>
                    <input
                      type="file"
                      id="logo-upload"
                      accept=".png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml"
                      onChange={handleLogoUpload}
                      style={{ display: 'none' }}
                    />
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      <label
                        htmlFor="logo-upload"
                        className="btn btn-primary"
                        style={{
                          cursor: uploadingLogo ? 'not-allowed' : 'pointer',
                          opacity: uploadingLogo ? 0.6 : 1,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        {uploadingLogo ? (
                          <>
                            <span className="spinner-border spinner-border-sm" style={{ width: '14px', height: '14px' }}></span>
                            Uploading...
                          </>
                        ) : (
                          '📤 Upload New Logo'
                        )}
                      </label>

                      {logoUrl && (
                        <button
                          onClick={handleDeleteLogo}
                          disabled={uploadingLogo}
                          className="btn"
                          style={{
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            opacity: uploadingLogo ? 0.6 : 1
                          }}
                        >
                          🗑️ Remove Custom Logo
                        </button>
                      )}
                    </div>

                    <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '10px' }}>
                      Recommended: Horizontal logo, transparent background, 400x100px minimum
                    </p>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Storefront default theme. Available on every tier: it costs nothing to
          serve and a buyer on a phone at night benefits from it regardless of
          what the seller pays. */}
      <div className="card" style={{ padding: '25px', marginBottom: '30px' }}>
        <h2 style={{ marginTop: 0, marginBottom: '10px', color: 'var(--color-brand-text)' }}>
          Default Theme
        </h2>
        <p style={{ fontSize: '14px', color: 'var(--color-text-light)', marginBottom: '20px' }}>
          The theme your public site starts in for a visitor who has not picked one yet.
          Visitors can always switch it themselves, and their choice is remembered on
          their device. System follows whatever their phone or computer is set to.
        </p>

        <label
          htmlFor="default-theme-select"
          style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}
        >
          Default theme
        </label>
        <select
          id="default-theme-select"
          value={defaultTheme}
          onChange={(e) => handleDefaultThemeChange(e.target.value)}
          style={{
            padding: '10px 12px',
            fontSize: '16px',
            minHeight: '44px',
            borderRadius: '6px',
            border: '1px solid var(--color-input-border)',
            backgroundColor: 'var(--color-input-bg)',
            color: 'var(--color-input-text)',
            minWidth: '220px'
          }}
        >
          <option value="system">System, follow the visitor's device</option>
          <option value="light">Light</option>
          <option value="dark">Dark</option>
        </select>
      </div>

      {/* Light Logo Section - Only for PRO+ with logo display enabled */}
      {logoUploadAllowed && !useTextLogo && (
        <div className="card" style={{ padding: '25px', marginBottom: '30px' }}>
          <h2 style={{ marginTop: 0, marginBottom: '10px', color: 'var(--color-brand-text)' }}>
            Logo for Dark Backgrounds
          </h2>
          <p style={{ fontSize: '14px', color: 'var(--color-text-light)', marginBottom: '20px' }}>
            Used anywhere your logo sits on a dark surface: hero sections, the footer,
            and the whole site in dark mode. A white or light colored logo works best.
            Optional. If you do not upload one, your regular logo is used instead.
          </p>

          {logoLightError && (
            <div className="alert alert-danger" style={{ marginBottom: '15px' }}>
              {logoLightError}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: '30px' }}>
            {/* Light Logo Preview */}
            <div style={{
              border: '2px dashed var(--color-border)',
              borderRadius: '8px',
              padding: '20px',
              backgroundColor: 'var(--color-primary)',
              minWidth: '200px',
              textAlign: 'center'
            }}>
              <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', margin: '0 0 10px 0' }}>
                {logoLightUrl ? 'Light Logo' : 'No Light Logo Set'}
              </p>
              {logoLightUrl ? (
                <img
                  src={logoLightUrl}
                  alt="Light Logo"
                  style={{
                    maxWidth: '180px',
                    maxHeight: '80px',
                    objectFit: 'contain'
                  }}
                />
              ) : (
                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', padding: '20px 0' }}>
                  Upload a white/light version<br />for dark backgrounds
                </div>
              )}
            </div>

            {/* Upload Controls */}
            <div style={{ flex: 1 }}>
              <input
                type="file"
                id="logo-light-upload"
                accept=".png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml"
                onChange={handleLogoLightUpload}
                style={{ display: 'none' }}
              />
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <label
                  htmlFor="logo-light-upload"
                  className="btn btn-primary"
                  style={{
                    cursor: uploadingLogoLight ? 'not-allowed' : 'pointer',
                    opacity: uploadingLogoLight ? 0.6 : 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {uploadingLogoLight ? (
                    <>
                      <span className="spinner-border spinner-border-sm" style={{ width: '14px', height: '14px' }}></span>
                      Uploading...
                    </>
                  ) : (
                    '📤 Upload Light Logo'
                  )}
                </label>

                {logoLightUrl && (
                  <button
                    onClick={handleDeleteLogoLight}
                    disabled={uploadingLogoLight}
                    className="btn"
                    style={{
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      opacity: uploadingLogoLight ? 0.6 : 1
                    }}
                  >
                    🗑️ Remove Light Logo
                  </button>
                )}
              </div>

              <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '10px' }}>
                Tip: Use a white or light-colored version of your logo for best visibility on dark backgrounds
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Favicon and social share image. Both are brand assets like the logos,
          so they sit on the same tier gate, but neither is tied to the header
          display choice: a tenant showing their company name as text still has
          a browser tab and still gets links shared. */}
      {logoUploadAllowed && (
        <div className="card" style={{ padding: '25px', marginBottom: '30px' }}>
          <h2 style={{ marginTop: 0, marginBottom: '10px', color: 'var(--color-brand-text)' }}>
            Favicon
          </h2>
          <p style={{ fontSize: '14px', color: 'var(--color-text-light)', marginBottom: '20px' }}>
            The small icon shown in the browser tab and in bookmarks. A square image
            works best, because this is displayed at about 16 pixels. Optional. If you
            do not upload one, your logo is used instead.
          </p>

          {faviconError && (
            <div className="alert alert-danger" style={{ marginBottom: '15px' }}>
              {faviconError}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: '30px', flexWrap: 'wrap' }}>
            <div style={{
              border: '2px dashed var(--color-border)',
              borderRadius: '8px',
              padding: '20px',
              backgroundColor: 'var(--color-surface-sunken)',
              minWidth: '160px',
              textAlign: 'center'
            }}>
              <p style={{ fontSize: '12px', color: 'var(--color-text-light)', margin: '0 0 10px 0' }}>
                {faviconUrl ? 'Current Favicon' : 'No Favicon Set'}
              </p>
              <img
                src={faviconUrl || logoUrl || defaultLogoUrl}
                alt="Favicon"
                style={{ width: '32px', height: '32px', objectFit: 'contain' }}
              />
            </div>

            <div style={{ flex: 1 }}>
              <input
                type="file"
                id="favicon-upload"
                accept=".ico,.png,.svg,image/png,image/svg+xml,image/x-icon,image/vnd.microsoft.icon"
                onChange={handleFaviconUpload}
                style={{ display: 'none' }}
              />
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <label
                  htmlFor="favicon-upload"
                  className="btn btn-primary"
                  style={{
                    cursor: uploadingFavicon ? 'not-allowed' : 'pointer',
                    opacity: uploadingFavicon ? 0.6 : 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {uploadingFavicon ? (
                    <>
                      <span className="spinner-border spinner-border-sm" style={{ width: '14px', height: '14px' }}></span>
                      Uploading...
                    </>
                  ) : (
                    '📤 Upload Favicon'
                  )}
                </label>

                {faviconUrl && (
                  <button
                    onClick={handleDeleteFavicon}
                    disabled={uploadingFavicon}
                    className="btn"
                    style={{
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      opacity: uploadingFavicon ? 0.6 : 1
                    }}
                  >
                    🗑️ Remove Favicon
                  </button>
                )}
              </div>

              <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '10px' }}>
                ICO, PNG, or SVG, max 1MB. Recommended: a square mark, 512x512px.
              </p>
            </div>
          </div>
        </div>
      )}

      {logoUploadAllowed && (
        <div className="card" style={{ padding: '25px', marginBottom: '30px' }}>
          <h2 style={{ marginTop: 0, marginBottom: '10px', color: 'var(--color-brand-text)' }}>
            Social Share Image
          </h2>
          <p style={{ fontSize: '14px', color: 'var(--color-text-light)', marginBottom: '20px' }}>
            The picture shown when someone shares a link to your site on Facebook,
            LinkedIn, or in a text message. Optional. If you do not upload one, your
            logo is used instead.
          </p>

          {ogImageError && (
            <div className="alert alert-danger" style={{ marginBottom: '15px' }}>
              {ogImageError}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: '30px', flexWrap: 'wrap' }}>
            <div style={{
              border: '2px dashed var(--color-border)',
              borderRadius: '8px',
              padding: '20px',
              backgroundColor: 'var(--color-surface-sunken)',
              minWidth: '200px',
              textAlign: 'center'
            }}>
              <p style={{ fontSize: '12px', color: 'var(--color-text-light)', margin: '0 0 10px 0' }}>
                {ogImageUrl ? 'Current Share Image' : 'No Share Image Set'}
              </p>
              <img
                src={ogImageUrl || logoUrl || defaultLogoUrl}
                alt="Social share"
                style={{ maxWidth: '200px', maxHeight: '105px', objectFit: 'contain' }}
              />
            </div>

            <div style={{ flex: 1 }}>
              <input
                type="file"
                id="og-image-upload"
                accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
                onChange={handleOgImageUpload}
                style={{ display: 'none' }}
              />
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <label
                  htmlFor="og-image-upload"
                  className="btn btn-primary"
                  style={{
                    cursor: uploadingOgImage ? 'not-allowed' : 'pointer',
                    opacity: uploadingOgImage ? 0.6 : 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {uploadingOgImage ? (
                    <>
                      <span className="spinner-border spinner-border-sm" style={{ width: '14px', height: '14px' }}></span>
                      Uploading...
                    </>
                  ) : (
                    '📤 Upload Share Image'
                  )}
                </label>

                {ogImageUrl && (
                  <button
                    onClick={handleDeleteOgImage}
                    disabled={uploadingOgImage}
                    className="btn"
                    style={{
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      opacity: uploadingOgImage ? 0.6 : 1
                    }}
                  >
                    🗑️ Remove Share Image
                  </button>
                )}
              </div>

              <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '10px' }}>
                PNG, JPG, or WebP, max 5MB. Recommended: 1200x630px.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Contact Information Section */}
      <div className="card" style={{ padding: '25px', marginBottom: '30px' }}>
        <h2 style={{ marginTop: 0, marginBottom: '10px', color: 'var(--color-brand-text)' }}>
          Contact Information
        </h2>
        <p style={{ fontSize: '14px', color: 'var(--color-text-light)', marginBottom: '20px' }}>
          Displayed in the header, footer, and legal documents
        </p>

        <div className="contact-settings-grid">
          <div>
            <label className="contact-settings-label" htmlFor="contact-companyName">
              Company Name
            </label>
            <input
              id="contact-companyName"
              className="contact-settings-input"
              type="text"
              value={contactInfo.companyName}
              onChange={(e) => handleContactInfoChange('companyName', e.target.value)}
              placeholder={resolvedContactInfo.companyName || 'DeedStack'}
            />
          </div>

          <div>
            <label className="contact-settings-label" htmlFor="contact-companyLegalName">
              Legal Name (for contracts)
            </label>
            <input
              id="contact-companyLegalName"
              className="contact-settings-input"
              type="text"
              value={contactInfo.companyLegalName}
              onChange={(e) => handleContactInfoChange('companyLegalName', e.target.value)}
              placeholder={resolvedContactInfo.companyLegalName || 'DeedStack LLC'}
            />
          </div>

          <div className="contact-settings-full">
            <label className="contact-settings-label" htmlFor="contact-companyAddress">
              Business Address
            </label>
            <input
              id="contact-companyAddress"
              className="contact-settings-input"
              type="text"
              value={contactInfo.companyAddress}
              onChange={(e) => handleContactInfoChange('companyAddress', e.target.value)}
              placeholder={resolvedContactInfo.companyAddress || '123 Main St, City, ST 12345'}
            />
          </div>

          <div>
            <label className="contact-settings-label" htmlFor="contact-companyPhone">
              Phone Number
            </label>
            <input
              id="contact-companyPhone"
              className="contact-settings-input"
              type="text"
              value={contactInfo.companyPhone}
              onChange={(e) => handleContactInfoChange('companyPhone', e.target.value)}
              placeholder={resolvedContactInfo.companyPhone || '(555) 555-5555'}
            />
          </div>

          <div>
            <label className="contact-settings-label" htmlFor="contact-companyEmail">
              Email Address
            </label>
            <input
              id="contact-companyEmail"
              className="contact-settings-input"
              type="email"
              value={contactInfo.companyEmail}
              onChange={(e) => handleContactInfoChange('companyEmail', e.target.value)}
              placeholder={resolvedContactInfo.companyEmail || 'info@example.com'}
            />
          </div>

          <div className="contact-settings-full">
            <label className="contact-settings-label" htmlFor="contact-websiteUrl">
              Website URL
            </label>
            <input
              id="contact-websiteUrl"
              className="contact-settings-input"
              type="text"
              value={contactInfo.websiteUrl}
              onChange={(e) => handleContactInfoChange('websiteUrl', e.target.value)}
              placeholder={resolvedContactInfo.websiteUrl || 'https://example.com'}
            />
          </div>

          {/*
            The name printed under the SELLER signature line on every contract
            this business issues. It is NOT the account contact -- the account
            contact is whoever signed up for DeedStack, and that is often not
            the officer authorised to execute a land contract. Leaving it empty
            is a supported answer, not a missing one, and the help text says so
            because an empty box in a settings page normally reads as "not
            filled in yet".
          */}
          <div className="contact-settings-full">
            <label className="contact-settings-label" htmlFor="contact-signerName">
              Contract Signer (name and title)
            </label>
            <p className="contact-settings-help">
              The person who signs contracts on behalf of {resolvedContactInfo.companyLegalName || resolvedContactInfo.companyName || 'your company'},
              printed under the seller signature line. Include the title on the same
              line, exactly as it should appear, for example{' '}
              <strong>Lisa Legendre, Managing Member</strong>.
            </p>
            <p className="contact-settings-help">
              This is <strong>not</strong> your account contact. Leave it empty to print a
              blank signature line, which is the correct form for a contract that has not
              been signed yet.
            </p>
            <input
              id="contact-signerName"
              className="contact-settings-input"
              type="text"
              value={contactInfo.signerName}
              onChange={(e) => handleContactInfoChange('signerName', e.target.value)}
              placeholder="Jane Doe, Managing Member"
              maxLength={120}
            />
          </div>

          <div className="contact-settings-full">
            <label className="contact-settings-label" htmlFor="contact-tagline">
              Tagline / Slogan
            </label>
            <p className="contact-settings-help">
              A short phrase that captures your business's identity (e.g., "Land Financing, Simplified")
            </p>
            <input
              id="contact-tagline"
              className="contact-settings-input"
              type="text"
              value={contactInfo.tagline}
              onChange={(e) => handleContactInfoChange('tagline', e.target.value)}
              placeholder={resolvedContactInfo.tagline || 'Land Financing, Simplified'}
              maxLength={100}
            />
          </div>

          <div className="contact-settings-full">
            <label className="contact-settings-label" htmlFor="contact-metaDescription">
              Site description for search and social previews
            </label>
            <p className="contact-settings-help">
              The sentence shown under your title in Google results and when someone
              shares a link to your site. Around 160 characters is the most that gets
              displayed. Leave it empty and one is written for you from your company
              name, which is what the placeholder shows.
            </p>
            <textarea
              id="contact-metaDescription"
              className="contact-settings-input contact-settings-textarea"
              value={contactInfo.metaDescription}
              onChange={(e) => handleContactInfoChange('metaDescription', e.target.value)}
              placeholder={resolvedContactInfo.metaDescription || ''}
              maxLength={160}
              rows={3}
            />
            <p
              className="contact-settings-help"
              style={{
                textAlign: 'right',
                margin: '4px 0 0 0',
                color: (contactInfo.metaDescription || '').length >= 160
                  ? 'var(--color-warning-text)'
                  : 'var(--color-text-muted)'
              }}
            >
              {(contactInfo.metaDescription || '').length}/160
            </p>
          </div>

          <div className="contact-settings-full">
            <label className="contact-settings-label" htmlFor="contact-businessHours">
              Business Hours
            </label>
            <p className="contact-settings-help">
              Displayed on the Contact page
            </p>
            <textarea
              id="contact-businessHours"
              className="contact-settings-input contact-settings-textarea"
              value={contactInfo.businessHours}
              onChange={(e) => handleContactInfoChange('businessHours', e.target.value)}
              placeholder={resolvedContactInfo.businessHours || 'Monday - Friday, 9:00 AM - 5:00 PM CST'}
              rows={3}
            />
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>

        {/* Color Pickers */}
        <div className="card" style={{ padding: '25px' }}>
          <h2 style={{ marginTop: 0, marginBottom: '20px', color: 'var(--color-brand-text)' }}>
            Color Configuration
          </h2>

          {hasCustomColors && (
            <div style={{
              backgroundColor: 'var(--color-success-bg)',
              padding: '10px 15px',
              borderRadius: '6px',
              marginBottom: '20px',
              fontSize: '14px',
              color: 'var(--color-success-text)'
            }}>
              Custom colors are currently active
            </div>
          )}

          {/* Quick Themes Dropdown */}
          <div style={{
            marginBottom: '25px',
            padding: '15px',
            backgroundColor: 'var(--color-surface-sunken)',
            borderRadius: '8px',
            border: '1px solid var(--color-border)'
          }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: 'var(--color-text)',
              fontSize: '15px'
            }}>
              Quick Themes
            </label>
            <p style={{
              fontSize: '12px',
              color: 'var(--color-text-light)',
              margin: '0 0 10px 0'
            }}>
              Select a pre-built color scheme or customize individual colors below
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <select
                value={getMatchingTheme(colors)}
                onChange={(e) => handleThemeChange(e.target.value)}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  border: '1px solid var(--color-border)',
                  borderRadius: '6px',
                  fontSize: '16px',
                  minHeight: '44px',
                  boxSizing: 'border-box',
                  backgroundColor: 'var(--color-surface)',
                  cursor: 'pointer'
                }}
              >
                {COLOR_THEMES.map(theme => (
                  <option key={theme.id} value={theme.id}>
                    {theme.name}
                  </option>
                ))}
              </select>
              {/* Theme color preview swatches */}
              {getMatchingTheme(colors) !== 'custom' && (
                <div style={{ display: 'flex', gap: '3px' }}>
                  {(() => {
                    const theme = COLOR_THEMES.find(t => t.id === getMatchingTheme(colors));
                    if (!theme?.colors) return null;
                    return (
                      <>
                        <div style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '4px',
                          backgroundColor: theme.colors.primaryColor,
                          border: '1px solid rgba(0,0,0,0.1)'
                        }} title="Primary" />
                        <div style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '4px',
                          backgroundColor: theme.colors.primaryDark,
                          border: '1px solid rgba(0,0,0,0.1)'
                        }} title="Primary Dark" />
                        <div style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '4px',
                          backgroundColor: theme.colors.accentColor,
                          border: '1px solid rgba(0,0,0,0.1)'
                        }} title="Accent" />
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>

          <div style={{
            borderBottom: '1px solid var(--color-border)',
            marginBottom: '20px',
            paddingBottom: '5px'
          }}>
            <span style={{ fontSize: '13px', color: 'var(--color-text-muted)', fontWeight: '500' }}>
              Individual Color Settings
            </span>
          </div>

          {colorFields.map(field => (
            <div key={field.key} style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '5px',
                fontWeight: '600',
                color: 'var(--color-text)'
              }}>
                {field.label}
              </label>
              <p style={{
                fontSize: '12px',
                color: 'var(--color-text-light)',
                margin: '0 0 8px 0'
              }}>
                {field.description}
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="color"
                  value={colors[field.key]}
                  onChange={(e) => handleColorChange(field.key, e.target.value)}
                  style={{
                    width: '60px',
                    /* A colour swatch opens a native picker rather than a
                       keyboard, so it needs no 16px font - but it is still a
                       tap target, and 40px missed the 44px floor. */
                    height: '44px',
                    padding: '0',
                    border: '2px solid var(--color-border)',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                />
                <input
                  type="text"
                  value={colors[field.key]}
                  onChange={(e) => handleColorChange(field.key, e.target.value)}
                  pattern="^#[0-9A-Fa-f]{6}$"
                  style={{
                    width: '100px',
                    padding: '8px 12px',
                    border: '1px solid var(--color-border)',
                    borderRadius: '6px',
                    fontFamily: 'monospace',
                    fontSize: '16px',
                    minHeight: '44px',
                    boxSizing: 'border-box'
                  }}
                />
                {defaults[field.key] && colors[field.key] !== defaults[field.key] && (
                  <button
                    onClick={() => handleColorChange(field.key, defaults[field.key])}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: 'var(--color-surface-sunken)',
                      border: '1px solid var(--color-border)',
                      borderRadius: '4px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                    title={`Reset to ${defaults[field.key]}`}
                  >
                    Reset
                  </button>
                )}
              </div>
            </div>
          ))}

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            gap: '10px',
            marginTop: '30px',
            paddingTop: '20px',
            borderTop: '1px solid var(--color-border)'
          }}>
            <button
              onClick={() => !demoDisabled && handleSave()}
              disabled={!hasChanges || saving || demoDisabled}
              className="btn btn-primary"
              style={{
                opacity: (!hasChanges || saving || demoDisabled) ? 0.6 : 1,
                cursor: (!hasChanges || saving || demoDisabled) ? 'not-allowed' : 'pointer'
              }}
              title={demoDisabled ? 'Disabled in demo mode' : ''}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              onClick={handleReset}
              disabled={saving || !hasCustomColors}
              className="btn btn-secondary"
              style={{
                opacity: (saving || !hasCustomColors) ? 0.6 : 1,
                cursor: (saving || !hasCustomColors) ? 'not-allowed' : 'pointer'
              }}
            >
              Reset to Defaults
            </button>
          </div>
        </div>

        {/* Live Preview */}
        <div className="card" style={{ padding: '25px' }}>
          <h2 style={{ marginTop: 0, marginBottom: '20px', color: 'var(--color-brand-text)' }}>
            Live Preview
          </h2>
          <p style={{ fontSize: '14px', color: 'var(--color-text-light)', marginBottom: '20px' }}>
            Changes preview in real-time. Save to apply site-wide.
          </p>

          {/* Preview Header */}
          <div style={{
            background: `linear-gradient(135deg, ${colors.primaryColor} 0%, ${colors.primaryDark} 100%)`,
            padding: '20px',
            borderRadius: '8px',
            color: 'white',
            marginBottom: '15px'
          }}>
            <h3 style={{ margin: '0 0 5px 0' }}>Sample Header</h3>
            <p style={{ margin: 0, opacity: 0.9, fontSize: '14px' }}>Navigation bar style</p>
          </div>

          {/* Preview Buttons */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
            <button style={{
              backgroundColor: colors.primaryColor,
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '6px',
              cursor: 'pointer'
            }}>
              Primary Button
            </button>
            <button style={{
              backgroundColor: colors.secondaryColor,
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '6px',
              cursor: 'pointer'
            }}>
              Secondary Button
            </button>
          </div>

          {/* Preview Card */}
          <div style={{
            backgroundColor: colors.lightBackground,
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '15px'
          }}>
            <h4 style={{ margin: '0 0 10px 0', color: colors.primaryColor }}>
              Feature Card Title
            </h4>
            <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: 'var(--color-text-light)' }}>
              This shows how section backgrounds and headings will appear with your chosen colors.
            </p>
            <a href="#preview" style={{ color: colors.accentColor, textDecoration: 'none' }}>
              Learn more →
            </a>
          </div>

          {/* Preview Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
            <div style={{
              backgroundColor: 'var(--color-surface)',
              padding: '15px',
              borderRadius: '8px',
              textAlign: 'center',
              border: '1px solid var(--color-border)'
            }}>
              <div style={{
                fontSize: '24px',
                fontWeight: 'bold',
                color: colors.primaryColor
              }}>
                24
              </div>
              <div style={{ fontSize: '12px', color: 'var(--color-text-light)' }}>Properties</div>
            </div>
            <div style={{
              backgroundColor: 'var(--color-surface)',
              padding: '15px',
              borderRadius: '8px',
              textAlign: 'center',
              border: '1px solid var(--color-border)'
            }}>
              <div style={{
                fontSize: '24px',
                fontWeight: 'bold',
                color: colors.secondaryColor
              }}>
                $12.5K
              </div>
              <div style={{ fontSize: '12px', color: 'var(--color-text-light)' }}>Revenue</div>
            </div>
            <div style={{
              backgroundColor: 'var(--color-surface)',
              padding: '15px',
              borderRadius: '8px',
              textAlign: 'center',
              border: '1px solid var(--color-border)'
            }}>
              <div style={{
                fontSize: '24px',
                fontWeight: 'bold',
                color: colors.accentColor
              }}>
                15
              </div>
              <div style={{ fontSize: '12px', color: 'var(--color-text-light)' }}>Active Loans</div>
            </div>
          </div>

          {/* Preview Footer */}
          <div style={{
            backgroundColor: colors.primaryDark,
            padding: '15px',
            borderRadius: '8px',
            color: 'white',
            textAlign: 'center',
            marginTop: '15px',
            fontSize: '14px'
          }}>
            Footer Preview
          </div>
        </div>
      </div>

      {/* Tips Section */}
      <div className="card" style={{ padding: '20px', marginTop: '30px' }}>
        <h3 style={{ margin: '0 0 15px 0', color: 'var(--color-brand-text)' }}>
          Tips for Choosing Colors
        </h3>
        <ul style={{ margin: 0, paddingLeft: '20px', color: 'var(--color-text-light)', lineHeight: '1.8' }}>
          <li><strong>Primary Color:</strong> Should match your brand's main color. This appears on headers and key buttons.</li>
          <li><strong>Primary Dark:</strong> A darker version of your primary color (typically 20-30% darker).</li>
          <li><strong>Secondary Color:</strong> A contrasting accent color for call-to-action buttons and highlights.</li>
          <li><strong>Accent Color:</strong> Used for links and interactive elements. Blue tones often work well for accessibility.</li>
          <li><strong>Light Background:</strong> A subtle tint (very light version) of your primary color for section backgrounds.</li>
        </ul>
      </div>
    </div>
  );
}

export default BrandingSettings;
